import { Switch, Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterSearch } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast, usePendingRows } from '../../../helpers'
import { IBlackList } from '../../../interfaces/BlackList/IBlackList'
import { BlackListService } from '../../../services/BlackList/BlackListService'
import BlackListPageModal from './BlackListPageModal'

export default function BlackListPage() {
  const service = new BlackListService()
  const toast = useAppToast()
  const [blackListed, setBlackListed] = useState<IBlackList[]>([])
  const [search, setSearch] = useState('')
  const [filtered, setFiltered] = useState<IBlackList[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<IBlackList | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState<IBlackList | null>(null)
  const savingRow = usePendingRows()
  const hasRole = useHasRole()
  const permissions = {
    addBlacklistEmail: hasRole('add_blacklist_email'),
    editBlacklistEmail: hasRole('edit_blacklist_email'),
    deleteBlacklistEmail: hasRole('delete_blacklist_email')
  }

  useEffect(() => {
    loadBlackListedMails()
  }, [])

  useEffect(() => {
    setFiltered(blackListed.filter((c) => c.email.toLowerCase().includes(search.toLowerCase())))
  }, [search, blackListed])

  const loadBlackListedMails = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      setBlackListed((await service.list())?.data || [])
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os e-mails', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateEnabled = async (enabled: boolean, id: number) => {
    try {
      if (enabled) {
        await service.enable(id)
      } else {
        await service.disable(id)
      }

      toast.success(`E-mail ${enabled ? 'bloqueado' : 'desbloqueado'} com sucesso`)

      setBlackListed((prev) => prev.map((item) => (item.id === id ? { ...item, is_active: enabled } : item)))
    } catch (error) {
      toast.error('Não foi possível atualizar o status')
    }
  }

  const handleDelete = async () => {
    if (!deleting) return

    try {
      await service.delete(deleting.id)

      toast.success('Excluído com sucesso', `O e-mail ${deleting.email} foi removido da blacklist.`)

      setBlackListed((prev) => prev.filter((item) => item.id !== deleting.id))
    } catch (error) {
      toast.error('Erro ao excluir', 'Não foi possível excluir o e-mail da blacklist.')
    } finally {
      setIsDeleteOpen(false)
      setDeleting(null)
    }
  }

  return (
    <>
      <PageTitle title="E-mails bloqueados" description="E-mails bloqueados para envios e cadastros" />
      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por e-mail"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>E-mail</Th>
              <Th>Motivo</Th>
              <Th>Criado por</Th>
              <Th>Criado em</Th>
              <Th>Ativo</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="e-mails" onRetry={loadBlackListedMails} />
            ) : filtered.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="e-mail" />
            ) : (
              filtered
                .sort((a, b) => a.id - b.id)
                .map((blackList) => (
                  <Tr key={blackList.id}>
                    <Td>{blackList.id}</Td>
                    <Td>{blackList.email}</Td>
                    <Td>
                      <ShortText text={blackList.reason || ''} beforeChars={25} afterChars={0} />
                    </Td>
                    <Td>
                      <EmptyCell value={blackList.created_by} />
                    </Td>
                    <Td>
                      <EmptyCell value={formatDateTime(blackList.created_at)} />
                    </Td>
                    <Td>
                      <Switch
                        isChecked={blackList.is_active}
                        onChange={(e) =>
                          savingRow.run(blackList.id, () => handleUpdateEnabled(e.target.checked, blackList.id))
                        }
                        isDisabled={savingRow.isPending(blackList.id) || !permissions.editBlacklistEmail}
                        title={blackList.is_active ? 'Desativar' : 'Ativar'}
                      />
                    </Td>
                    <Td>
                      {permissions.editBlacklistEmail && (
                        <ActionButton
                          action="edit"
                          onClick={() => {
                            setEditing(blackList)
                            setIsFormOpen(true)
                          }}
                        />
                      )}
                      {permissions.deleteBlacklistEmail && (
                        <ActionButton
                          action="delete"
                          onClick={() => {
                            setDeleting(blackList)
                            setIsDeleteOpen(true)
                          }}
                        />
                      )}
                    </Td>
                  </Tr>
                ))
            )}
          </Tbody>

          <ConfirmDialog
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={handleDelete}
            action="delete"
            entity="e-mail"
            name={deleting?.email}
            description={
              <>
                Tem certeza que deseja remover o e-mail <strong>{deleting?.email}</strong> da blacklist?
              </>
            }
          />
        </Table>
      </TableContainer>
      <BlackListPageModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          loadBlackListedMails()
        }}
        data={editing}
      />

      {permissions.addBlacklistEmail && (
        <ActionFab
          label="Adicionar e-mail à blacklist"
          onClick={() => {
            setEditing(null)
            setIsFormOpen(true)
          }}
        />
      )}
    </>
  )
}
